import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Badge } from '@/components/base/badges/badges';
import { Button, ExternalLinkButton } from '@/components/base/buttons/button';
import { ExternalLink } from '@/components/base/link/link';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { notify } from '@/components/base/notifications/notifications';

import {
  disconnectSurveyMonkey,
  getTeamIntegrations,
} from '../-utils/team-integrations-server';
import { SettingsSection } from '../../settings/-components/settings-section';
import { WORKSPACE_SETTINGS_PAGES } from '../../settings/-utils/legacy-settings-url';

const INTEGRATIONS_HELP =
  'https://help.respondent.io/en/articles/6945908-integrations';

export const Route = createFileRoute(
  '/researchers/_authenticated/organization-settings/team/integrations',
)({
  component: TeamIntegrationsPage,
  head: () => ({ meta: [{ title: t`Team integrations` }] }),
});

function TeamIntegrationsPage() {
  const { activeDashboard } = Route.useRouteContext();
  const { env } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const queryKey = [
    'workspace-settings',
    'team',
    activeDashboard.team ?? '',
    'integrations',
  ] as const;
  const integrations = useQuery({
    queryKey,
    queryFn: () => getTeamIntegrations(),
    // Connecting happens in another tab; pick it up on return.
    refetchOnWindowFocus: true,
  });

  const disconnect = useMutation({
    mutationFn: async () => {
      const result = await disconnectSurveyMonkey();
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: async () => {
      setIsConfirmOpen(false);
      notify({ title: t`SurveyMonkey disconnected`, color: 'success' });
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) =>
      notify({
        title: t`Couldn't disconnect SurveyMonkey`,
        description: error.message,
        color: 'error',
      }),
  });

  const legacyIntegrationsUrl =
    env.WEBSITE_URL && activeDashboard.organization && activeDashboard.team
      ? `${env.WEBSITE_URL}/dashboard/organizations/${activeDashboard.organization}/teams/${activeDashboard.team}${WORKSPACE_SETTINGS_PAGES['team/integrations']}`
      : undefined;

  return (
    <SettingsSection
      title={t`Team integrations`}
      description={t`Connect the tools your team runs studies in.`}
      helpUrl={INTEGRATIONS_HELP}
    >
      {integrations.isError ? (
        <AlertFloating
          color="error"
          title={t`We couldn't load this team's integrations`}
          description={t`Refresh the page to try again.`}
          confirmLabel=""
        />
      ) : integrations.isPending ? (
        <div className="h-28 animate-pulse rounded-lg bg-secondary" />
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg p-4 ring-1 ring-secondary">
          <div className="flex max-w-xl flex-col gap-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-primary">
                <Trans>SurveyMonkey</Trans>
              </p>
              {integrations.data.surveyMonkeyConnected && (
                <Badge type="pill-color" color="success" size="sm">
                  <Trans>Connected</Trans>
                </Badge>
              )}
            </div>
            <p className="text-sm text-tertiary">
              <Trans>
                Easy access to your surveys and responses. Automate participant
                tracking with Respondent and SurveyMonkey.
              </Trans>{' '}
              <ExternalLink href={INTEGRATIONS_HELP} target="_blank">
                <Trans>Learn more</Trans>
              </ExternalLink>
            </p>
          </div>
          {integrations.data.canManage &&
            (integrations.data.surveyMonkeyConnected ? (
              <Button
                color="secondary-destructive"
                size="sm"
                onClick={() => setIsConfirmOpen(true)}
              >
                <Trans>Disconnect</Trans>
              </Button>
            ) : (
              legacyIntegrationsUrl && (
                // SurveyMonkey returns to the legacy page; see the server
                // functions for why connecting still happens there.
                <ExternalLinkButton
                  href={legacyIntegrationsUrl}
                  target="_blank"
                  color="secondary"
                  size="sm"
                >
                  <Trans>Connect</Trans>
                </ExternalLinkButton>
              )
            ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={t`Disconnect SurveyMonkey?`}
        description={t`Your team won't be able to pick SurveyMonkey surveys for projects until it's connected again.`}
        confirmLabel={t`Disconnect`}
        isDestructive
        isLoading={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </SettingsSection>
  );
}
